/**
 * One Word Training records — per-user, per-word best metrics + running averages.
 * Firestore: users/{uid}/oneWordRecords/{wordKey}
 *
 * The aggregate record is the source for the live "record" comparison and for the
 * personal "+X% vs your average" deltas on the completion screen. Per-race time-series
 * data lives in users/{uid}/oneWordRaces (see oneWordRacesService.ts).
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { describeFirestoreError } from '@/firebase/firestore';
import { FIREBASE_COLLECTIONS } from '@/types/firebase';

export interface OneWordRecord {
  bestLapMs: number;       // fastest single-lap completion (all-time best)
  bestStreak: number;      // longest run of clean laps in a row (all-time best)
  avgLapMs: number;        // average ms per lap across all-time
  totalLaps: number;       // cumulative laps completed (any quality)
  totalTimeMs: number;     // cumulative typing time (sum of lap times)
  lastTrainedAt: number;

  // Running aggregates across completed races (sessions) — power personal "+X% vs avg".
  // All optional so historical docs remain valid.
  racesCount?: number;
  sumRaceBestLapMs?: number;   // sum of each race's best lap → avg = sum / racesCount
  sumRaceBestStreak?: number;  // sum of each race's best streak
  sumRaceLaps?: number;        // sum of each race's lap count
  avgRaceBestLapMs?: number;   // derived, stored for convenience
  avgRaceBestStreak?: number;  // derived
  avgRaceLaps?: number;        // derived
  letterMistakesTotal?: Record<string, number>; // cumulative mistakes per letter, all-time
}

const cacheKey = (uid: string, wordKey: string) => `kbh-onewordrec-${uid}-${wordKey}`;

export const normalizeWordKey = (word: string): string =>
  word.trim().toLowerCase().replace(/\s+/g, '_');

export const getOneWordRecord = async (
  uid: string,
  word: string
): Promise<OneWordRecord | null> => {
  if (!uid || !word) return null;
  const key = normalizeWordKey(word);

  const cached = localStorage.getItem(cacheKey(uid, key));
  if (cached) {
    try {
      return JSON.parse(cached) as OneWordRecord;
    } catch {
      // fall through
    }
  }

  try {
    const snap = await getDoc(
      doc(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.ONE_WORD_RECORDS, key)
    );
    if (snap.exists()) {
      const data = snap.data() as OneWordRecord;
      localStorage.setItem(cacheKey(uid, key), JSON.stringify(data));
      return data;
    }
  } catch (e) {
    console.warn('oneWordRecords: read failed', e);
    describeFirestoreError(`getOneWordRecord ${key}`, e);
  }
  return null;
};

/**
 * Low-level writer for the aggregate record (Firestore + localStorage cache).
 * Use saveOneWordRace() (oneWordRacesService) for the normal end-of-race path.
 */
export const writeOneWordRecord = async (
  uid: string,
  word: string,
  record: OneWordRecord
): Promise<void> => {
  if (!uid || !word) return;
  const key = normalizeWordKey(word);
  try {
    await setDoc(
      doc(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.ONE_WORD_RECORDS, key),
      { ...record, updatedAt: serverTimestamp() }
    );
    localStorage.setItem(cacheKey(uid, key), JSON.stringify(record));
  } catch (e) {
    console.warn('oneWordRecords: write failed', e);
    describeFirestoreError(`writeOneWordRecord ${key}`, e);
  }
};

/**
 * Merge a single race's metrics into the previous aggregate record.
 * Pure function — no I/O — so it is easy to test and reuse.
 */
export const mergeRaceIntoRecord = (
  prev: OneWordRecord | null,
  race: {
    bestLapMs: number;
    bestStreak: number;
    lapsCompleted: number;
    totalTimeMs: number;
    letterMistakes: Record<string, number>;
  }
): OneWordRecord => {
  const totalLaps = (prev?.totalLaps || 0) + race.lapsCompleted;
  const totalTimeMs = (prev?.totalTimeMs || 0) + race.totalTimeMs;

  const racesCount = (prev?.racesCount || 0) + 1;
  const sumRaceBestLapMs = (prev?.sumRaceBestLapMs || 0) + (race.bestLapMs > 0 ? race.bestLapMs : 0);
  const sumRaceBestStreak = (prev?.sumRaceBestStreak || 0) + race.bestStreak;
  const sumRaceLaps = (prev?.sumRaceLaps || 0) + race.lapsCompleted;

  // Cumulative per-letter mistakes
  const letterMistakesTotal: Record<string, number> = { ...(prev?.letterMistakesTotal || {}) };
  for (const [letter, count] of Object.entries(race.letterMistakes)) {
    letterMistakesTotal[letter] = (letterMistakesTotal[letter] || 0) + count;
  }

  return {
    bestLapMs:
      race.bestLapMs > 0 && (!prev || prev.bestLapMs <= 0 || race.bestLapMs < prev.bestLapMs)
        ? race.bestLapMs
        : prev?.bestLapMs ?? race.bestLapMs,
    bestStreak: Math.max(prev?.bestStreak ?? 0, race.bestStreak),
    avgLapMs: totalLaps > 0 ? Math.round(totalTimeMs / totalLaps) : 0,
    totalLaps,
    totalTimeMs,
    lastTrainedAt: Date.now(),
    racesCount,
    sumRaceBestLapMs,
    sumRaceBestStreak,
    sumRaceLaps,
    avgRaceBestLapMs: sumRaceBestLapMs > 0 && racesCount > 0 ? Math.round(sumRaceBestLapMs / racesCount) : 0,
    avgRaceBestStreak: racesCount > 0 ? sumRaceBestStreak / racesCount : 0,
    avgRaceLaps: racesCount > 0 ? sumRaceLaps / racesCount : 0,
    letterMistakesTotal,
  };
};
